/*
 *  External entity selector.
 *  Allows user to select from external entities defined for the fund.
 *  Renders the external data based on the predefined form under integration settings.
 */

import React from 'react';

import Base from './base.jsx'; 
import Setting from "./setting.jsx";

import EntityLookupSelect from './entity_lookup_select.jsx'
import Resolver from '../component_resolver.jsx'

import { resolveComponentProps } from '../form_viewer.jsx';
import { fetchJson } from '../../../helpers/fetch.js';
import LoadingIndicator from '../../common/loading_indicator.jsx';

import {
  integration_list_entity_forms_path,
  integration_get_entity_form_path,
  integration_list_endpoints_path,
  integration_get_entity_path
} from '~/routes';

export default class AdaliaMEExternalEntitySelector extends Base {
  static get CATEGORY() {
    return 'other';
  }

  static get TYPE() {
      return 'adalia_me_external_entity_selector';
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
        rows: [],
        label: 'label',
        placeholder: 'placeholder',
        description: 'description',
        includeBlank: false,
        blankOptionLabel: 'blankOptionLabel',
        lookupComponentType: 'select',
        lookupComponent: {...EntityLookupSelect.defaultContent(language), }
    });
  }


  static get TRANSLATED_ATTRIBUTES() {
    return {
        label: 'field_default_label',
        placeholder: 'field_default_placeholder',
        description: 'field_default_description',
        help: null,
        unit: null
    };
  }

  static nestedComponents(parentComponentContent) {
    return parentComponentContent.lookupComponent ? [parentComponentContent.lookupComponent] : []
  }


  static icon(){
    return <i className="fa fa-sitemap"></i>;
  }

  constructor(props) {
      super(props);

      this.state = {
        endpointOptions: null, // Null until fetched
        lookupComponents: ['select'],
        availableEntities: null, // Null until fetched
        entityContent: {},
        answers: {},
        loading: false
      }
  }

  static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = '') {
    let namesOrIds = [...super.resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope)]
    
    // If options depend on other field
    if (content.lookupComponent.dependentOnField && content.lookupComponent.dependentOnField != '' ) {
      namesOrIds.push(content.lookupComponent.dependentOnField)
    }
    return namesOrIds
  }

  getEntityForms = () => {
    if (this.props.content?.endpoint && this.props.content?.endpoint != '') {
      fetchJson(integration_list_entity_forms_path({object_type: this.props.content.endpoint}), {useCache: true}).then((res) => {
        this.setState({availableEntities: res.forms}, () => {
          this.setDefaultFromAsyncOptions('entity');
        });
      })
    }
  }

  getEntityFormDetails = () => {
    if (this.props.content?.entity && this.props.content?.entity != '') {
      fetchJson(integration_get_entity_form_path({form_id: this.props.content.entity}), {useCache: true}).then((res) => {
        this.setState({entityContent: res.content.question_data.pages[0].components[0]});
      });
    }
  }

  getEndpoints = () => {
    fetchJson(integration_list_endpoints_path(), {useCache: true}).then((res) => {
      this.setState({endpointOptions: res.endpoints},() => {
        this.setDefaultFromAsyncOptions('endpoint');
        if(!this.props.content.lookupComponent.endpoint){
          this.updateEndpoint();
        }  
      });
    });
  }

  setDefaultFromAsyncOptions = (field) => {
    if (!this.props.content[field]) {
      if ( typeof this.props.updateSettingsCallback == 'function') {
        this.updateSettings();
      }
    }
  }

  handleEndpointUpdate = (e) => {
    this.updateEndpoint();
    e.preventDefault();
  }

  updateEndpoint = () => {
    // The endpoint needs to be updated to lookup component as well
    // use the first option if component isn't rendered (only happens on initial component drop)
    var endpointCurrentValue = jQuery(this.refs[this.settingsFormRef()]).find('select[name="endpoint"]').val() || (this.state.endpointOptions || [])[0];
    var lookupComponent = {...EntityLookupSelect.defaultContent(this.props.language), endpoint: endpointCurrentValue}

    const content = {...this.props.content, endpoint: endpointCurrentValue, entity: null, lookupComponent: lookupComponent }
    this.props.updateSettingsCallback(this.props.content.id, content, true);
  }

  componentDidMount(){
    if (this.props.mode == 'settings'){
      this.getEndpoints();
      if(!this.props.content.lookupComponent.endpoint){
        this.updateEndpoint();
      }
    }

    if (this.props.content && this.props.content.endpoint && this.props.content.endpoint != '') {
      this.getEntityForms();
    }

    if (this.props.content && this.props.content.entity && this.props.content.entity != '') {
      this.getEntityFormDetails();
    }

    if (this.props.answers && this.props.content.lookupComponent && this.props.answers[this.props.content.lookupComponent.name] ) {
      this.loadEntityDetails(this.props.answers[this.props.content.lookupComponent.name]);
    }

  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.content?.endpoint != this.props.content?.endpoint){
      this.getEntityForms();
    }
    if (prevProps.content?.entity != this.props.content?.entity){
      this.getEntityFormDetails();
    }

    if (this.props.answers && prevProps.answers && prevProps.answers[this.props.content.lookupComponent.name] != this.props.answers[this.props.content.lookupComponent.name] ) {
      this.loadEntityDetails(this.props.answers[this.props.content.lookupComponent.name]);
    }
  }

  renderView() {

    let loadedEntityId = (this.props?.answers || {})[(this.props.content?.lookupComponent || {}).name];
    let rows = (this.state.entityContent.rows || []).map((row, rowIdx) => {
        if (row.every(col => !col.component)) return null;

        let cols = row.map((col, colIdx) => {
            let component = null;

            if (col.component) {
                component = Resolver.createElement(col.component.type, 
                  resolveComponentProps(
                    {...col.component, lockEditWhenPrefilled: true, disablePrefillEditLocks: false, disabled: true}, 
                    this.props, 
                    {
                      swapComponentsOnDrop: false, 
                      // Select external entity component when clicking on entity
                      selectCallback: (_) => { this.props.selectCallback(this.props.content) }, 
                      // selectedId: this.props.id,
                      fieldScope: this.props.fieldScope,
                      repeatedFieldScope: this.props.repeatedFieldScope,
                      answers: this.state.answers,
                      prefillAnswers: this.state.answers,
                      disabled: true
                    }
                    )
                );
            }
            return (
                <div key={`${loadedEntityId} ${colIdx} ${this.state.loading}`} className="grid-col" style={{flex: col.span}} data-flex={col.span}>
                    {component}
                </div>
            );
        });

        return (
            <div key={`${loadedEntityId} ${rowIdx} ${this.state.loading}`} className="grid-row">
                {cols}
            </div>
        );
    });

    const lookupComponent = this.props.content?.lookupComponent  ? Resolver.createElement('adalia_me_entity_lookup_select', 
      resolveComponentProps(this.props.content.lookupComponent, this.props, {
        swapComponentsOnDrop: false, 
        selectCallback: this.props.selectCallback, 
        selectedId: this.props.selectedId,
        deleteRepeatableRegionAnswerCallback: this.props.deleteRepeatableRegionAnswerCallback,
        fieldScope: this.props.fieldScope,
        repeatedFieldScope: this.props.repeatedFieldScope,
        endpoint: this.props.content.endpoint
      })) : null

    return (
        <div className="wrapper" data-endpoint={this.props.content.endpoint}>
            {lookupComponent}
            { !(rows.every(r => !r)) && !this.props.content.hideLinkedForm && 
              <div className="external_entity_wrapper">
                {this.state.loading && <LoadingIndicator asOverlay={true} />}
                {rows}
              </div>
            }
        </div>
    );
  }

  loadEntityDetails = (external_entity_id) => {
    if(!external_entity_id || (Array.isArray(external_entity_id) && !external_entity_id.length)){
      return;
    }
    this.setState({loading: true});
    fetchJson(integration_get_entity_path({entity_id: external_entity_id, form_id: this.props.content.entity})).then(res => {
      this.setState({answers: res, loading: false})
    })
  }

  renderSettings() {
    if (this.state.endpointOptions === null) {
      return <LoadingIndicator />
    }

    return (
      <div>
        <Setting.Dropdown 
          name="endpoint" 
          label={I18n.t('endpoint', {scope: 'fe'})}
          value={this.props.content.endpoint} 
          onChange={this.handleEndpointUpdate} 
          options={this.state.endpointOptions}
          description={I18n.t('field_external_entity_selector_endpoint_description', {scope: 'fe'})}
          disabled={ this.state.endpointOptions === null}
        />        
        <Setting.Dropdown 
          name="entity" 
          label={I18n.t('entity', {scope: 'fe'})} 
          value={this.props.content.entity} 
          onChange={this.updateSettings} 
          options={this.state.availableEntities}
          description={I18n.t('field_external_entity_selector_entity_description', {scope: 'fe'})}
          disabled={!this.props.content.endpoint || this.state.availableEntities === null}
        /> 

        <Setting.Dropdown 
          name="lookupComponentType" 
          label={I18n.t('lookup_component', {scope: 'fe'})} 
          value={this.props.content.lookupComponentType} 
          onChange={this.handleLookupComponentChange} 
          options={this.state.lookupComponents.map(o => [o, I18n.t('lookup_component_' + o, {scope: 'fe'})])}
          description={I18n.t('field_external_entity_selector_lookup_description', {scope: 'fe'})}
          disabled={!this.props.content.endpoint}
        />
        <Setting.Checkbox 
          name="hideLinkedForm" 
          label={I18n.t('hide_linked_form', {scope: 'fe'})} 
          value={this.props.content.hideLinkedForm} 
          onChange={this.updateSettings}
        />
      </div> 
    )
  }


}

